import { QueryClient } from '@tanstack/react-query';

/**
 * Single QueryClient shared by the entire ops-console app.
 *
 * Defaults tuned for operator workflows:
 *   - `staleTime: 30_000` — operator tables can tolerate 30s staleness
 *     before we re-fetch in the background. Mutation callbacks invalidate
 *     explicitly, so waiting longer is fine for view-only screens.
 *   - `retry: false` — our error envelopes already encode `retryable`, and
 *     surfacing the first error is better than silently swallowing
 *     transient 5xx's in a control plane. Tests also depend on this:
 *     retries force every assertion to await multiple ticks.
 *   - `refetchOnWindowFocus: false` — operator may tab between pages
 *     while reading a probe result; a focus-driven refetch would clobber
 *     in-flight state.
 *
 * The client is exported from a factory so tests can mint a fresh
 * instance per render and avoid cross-test cache bleed.
 */
export function createOpsQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: false,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
